/**
 * Component for displaying a single tee time with enhanced visual appeal
 */
import React from 'react';
import { TeeTime, GolfCourse } from '../types';
import { getWeatherIconUrl } from '../assets/icons/weather-icons';
import '../styles/TeeTimeCard.css';

/**
 * Props for the TeeTimeCard component
 */
interface TeeTimeCardProps {
  teeTime: TeeTime;
  course: GolfCourse;
  isSelected: boolean;
  onSelect: (teeTime: TeeTime) => void;
}

/**
 * TeeTimeCard component displays a single tee time with weather and pricing information
 * @param props Component props
 * @returns React component
 */
const TeeTimeCard: React.FC<TeeTimeCardProps> = ({ teeTime, course, isSelected, onSelect }) => {
  const { startTime, endTime, availableSpots, isFullyBooked, price, specialOffer, weather } = teeTime;
  
  /**
   * Formats the tee time display
   * @returns Formatted time range
   */
  const formatTeeTime = (): string => {
    // Convert 24-hour format to 12-hour format with AM/PM
    const formatTime = (timeStr: string) => {
      const [hours, minutes] = timeStr.split(':').map(Number);
      const period = hours >= 12 ? 'PM' : 'AM';
      const displayHours = hours % 12 || 12;
      return `${displayHours}:${minutes.toString().padStart(2, '0')} ${period}`;
    };
    
    return `${formatTime(startTime)} - ${formatTime(endTime)}`;
  };
  
  /**
   * Gets the appropriate CSS class based on spots availability
   * @returns CSS class name
   */
  const getAvailabilityClass = (): string => {
    if (isFullyBooked) return 'fully-booked';
    if (availableSpots === 1) return 'almost-full';
    if (availableSpots <= 2) return 'limited';
    return 'available';
  };
  
  /**
   * Gets a text description of availability
   * @returns Text description
   */
  const getAvailabilityText = (): string => {
    if (isFullyBooked) return 'Fully Booked';
    if (availableSpots === 1) return 'Last Spot!';
    return `${availableSpots} spots available`;
  };
  
  /**
   * Handles click on the tee time card
   */
  const handleClick = () => {
    if (!isFullyBooked) {
      onSelect(teeTime);
    }
  };
  
  /**
   * Formats the temperature display with proper unit
   * @param temp Temperature in Fahrenheit
   * @returns Formatted temperature string
   */
  const formatTemperature = (temp: number): string => {
    return `${Math.round(temp)}°F`;
  };
  
  /**
   * Gets an icon for the tee time based on time of day
   * @returns Icon name
   */
  const getTimeIcon = (): string => {
    const hour = parseInt(startTime.split(':')[0], 10);
    
    if (hour < 8) return '🌅'; // Early morning
    if (hour < 12) return '☀️'; // Morning
    if (hour < 16) return '🌤️'; // Afternoon
    return '🌇'; // Evening
  };
  
  return (
    <div 
      className={`tee-time-card ${getAvailabilityClass()} ${isSelected ? 'selected' : ''}`}
      onClick={handleClick}
    >
      <div className="tee-time-card-content">
        <div className="tee-time-header">
          <span className="time-icon">{getTimeIcon()}</span>
          <span className="tee-time">{formatTeeTime()}</span>
          {weather && (
            <div className="weather-info" title={`${weather.condition}, ${formatTemperature(weather.temperature)}, Wind: ${weather.windSpeed} mph`}>
              <img 
                src={getWeatherIconUrl(weather.icon)} 
                alt={weather.condition} 
                className="weather-icon" 
              />
              <span className="temperature">{formatTemperature(weather.temperature)}</span>
            </div>
          )}
        </div>
        
        <div className="tee-time-details">
          <div className="price-container">
            <span className="price-label">Price</span>
            <span className="price-value">${price}</span>
            <span className="per-person">per player</span>
          </div>
          
          <div className={`availability ${getAvailabilityClass()}`}>
            {getAvailabilityText()}
          </div>
        </div>
        
        {specialOffer && (
          <div className="special-offer">
            <span className="offer-icon">🏆</span>
            <span className="offer-text">{specialOffer}</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default TeeTimeCard;
